import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
} from "firebase/auth";
import { firebaseAuth } from "src/app/firebase";

@Component({
    selector: "app-totp-setup",
    templateUrl: "./totp-setup.component.html",
    styleUrls: ["./totp-setup.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TotpSetupComponent {
  private formBuilder = inject(FormBuilder);
  private snackBar = inject(MatSnackBar);
  private dialogRef = inject(MatDialogRef<TotpSetupComponent>);

  loading = signal<boolean>(false);
  qrCodeUrl = signal<string | null>(null);

  dialogTitle = $localize`Setting Up Google Authenticator`;

  private totpSecret: TotpSecret | null = null;

  totpForm = this.formBuilder.group({
    code: new FormControl<string>("", [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  async generateSecret() {
    this.loading.set(true);
    try {
      const user = firebaseAuth.currentUser;

      if (!user) {
        throw new Error($localize`You must be logged in to set up MFA`);
      }

      const multiFactorSession = await multiFactor(user).getSession();
      this.totpSecret =
        await TotpMultiFactorGenerator.generateSecret(multiFactorSession);

      const url = this.totpSecret.generateQrCodeUrl(
        user.email || $localize`User`,
        "AdaptiveServiceAdmin",
      );

      this.qrCodeUrl.set(url);
    } catch (error) {
      console.error($localize`Secret generation error`, error);
      this.snackBar.open($localize`The configuration could not be started.`, "", {
        duration: 2500,
        panelClass: "app-snackbar-alert",
      });
    } finally {
      this.loading.set(false);
    }
  }

  async verifyAndEnroll() {
    if (!this.totpSecret || this.totpForm.invalid) return;

    this.loading.set(true);
    const code = this.totpForm.value.code!;

    try {
      const user = firebaseAuth.currentUser;

      if (!user) throw new Error($localize`No user logged in`);

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        this.totpSecret,
        code,
      );

      await multiFactor(user).enroll(assertion, $localize`Authenticator App`);
      this.snackBar.open(
        $localize`Two-step verification has been successfully enabled!`,
        "",
        { duration: 2500, panelClass: "app-snackbar-success" },
      );

      this.dialogRef.close(true);
    } catch (error) {
      console.error("Verification error", error);
      this.snackBar.open($localize`Invalid code. Please try again.`, "", {
        duration: 2500,
        panelClass: "app-snackbar-alert",
      });
    } finally {
      this.loading.set(false);
    }
  }

  cancelSetup() {
    this.dialogRef.close(false);
  }
}
